
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FlightBooking } from '@/api/entities';
import { Passenger } from '@/api/entities';
import { UploadFile, ExtractDataFromUploadedFile } from '@/api/integrations';
import { Button } from '@/components/ui/button';
import { Sparkles, Loader2, Save, ArrowRight, Pencil, ImageIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl, getServiceFeeRate } from '@/utils';
import { initiateSlicePayFlow } from '@/api/functions';

import FileUploadZone from '../components/upload/FileUploadZone';
import FlightCard from '../components/flight/FlightCard';
import PassengerForm from '../components/passenger/PassengerForm';
import RoundtripFlightCard from '../components/flight/RoundtripFlightCard';
import EditFlightModal from '../components/flight/EditFlightModal';

export default function Upload() {
  const navigate = useNavigate();
  const [dragActive, setDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedFlight, setExtractedFlight] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState('upload');
  const [savedFlightId, setSavedFlightId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const handleFileSelect = async (file) => {
    setIsProcessing(true);
    setError(null);

    try {
      // Upload the file first with retry logic
      let uploadResult;
      let retryCount = 0;
      const maxRetries = 2; // Maximum number of retries

      while (retryCount <= maxRetries) {
        try {
          uploadResult = await UploadFile({ file });
          break; // Success, exit retry loop
        } catch (uploadError) {
          retryCount++;
          if (retryCount > maxRetries) {
            // Show the actual error message to user
            throw new Error(`Upload failed: ${uploadError.message || 'Unknown error'}`);
          }
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 2000 * retryCount));
        }
      }

      const { file_url, file_base64 } = uploadResult;
      setUploadedImage(file_url);

      // Extract flight data using AI with enhanced schema for connections
      let extractResult;
      retryCount = 0; // Reset retry count for extraction
      
      while (retryCount <= maxRetries) {
        try {
          extractResult = await ExtractDataFromUploadedFile({
            file_url,
            file_base64,
            json_schema: {
              type: "object",
              properties: {
                flights: {
                  type: "array",
                  items: {
                    type: "object",
                    properties: {
                      marketing_airline: {
                        type: "string",
                        description: "The airline that sold the ticket (e.g., 'Delta', 'United'). If unclear, use the most prominent airline name visible."
                      },
                      operating_airline: {
                        type: "string",
                        description: "The airline actually operating this specific flight segment. May be different from marketing airline for codeshare flights."
                      },
                      flight_number: {
                        type: "string",
                        description: "Flight number for this specific segment (e.g., 'DL123'). Use 'unknown' if not clearly visible."
                      },
                      departure_city: {
                        type: "string",
                        description: "Full name of the departure city for this segment (e.g., 'New York'). Do not use airport codes like 'JFK'."
                      },
                      arrival_city: {
                        type: "string",
                        description: "Full name of the arrival city for this segment (e.g., 'London' or 'Paris'). Do not use airport codes like 'LHR'."
                      },
                      departure_date: {
                        type: "string",
                        description: "Departure date for this segment in YYYY-MM-DD format. If year not visible, assume current year."
                      },
                      departure_time: {
                        type: "string",
                        description: "Departure time for this segment in HH:MM format (24-hour). Leave empty if not visible."
                      },
                      arrival_date: {
                        type: "string",
                        description: "Arrival date for this segment in YYYY-MM-DD format. If not visible, use departure date."
                      },
                      arrival_time: {
                        type: "string",
                        description: "Arrival time for this segment in HH:MM format (24-hour). Leave empty if not visible."
                      },
                      connection_duration: {
                        type: "string",
                        description: "The layover time before this flight segment, if visible (e.g., '2h 30m'). Leave empty for the first flight or if not specified."
                      },
                      passenger_name: {
                        type: "string",
                        description: "Passenger name if visible, otherwise 'unknown'"
                      },
                      seat_class: {
                        type: "string",
                        enum: ["economy", "premium_economy", "business", "first"],
                        description: "Travel class, default to 'economy'"
                      },
                      total_price: {
                        type: "number",
                        description: "For connecting flights, only put the total price on the FIRST segment of the journey. For subsequent segments of the same journey, use 0. Extract the final total price of the entire booking."
                      },
                      confirmation_code: {
                        type: "string",
                        description: "Booking confirmation/PNR code if visible, otherwise 'unknown'"
                      }
                    },
                    required: ["marketing_airline", "departure_city", "arrival_city", "departure_date"]
                  },
                  description: "CRITICAL: Extract ALL flight segments separately. If a flight shows 'New York to Paris with 1 stop in London', create TWO separate flight objects: 1) New York → London, 2) London → Paris. For roundtrip flights, extract each direction separately. Each flight segment should be its own object in the array."
                }
              },
              required: ["flights"]
            }
          });
          break; // Success, exit retry loop
        } catch (extractError) {
          retryCount++;
          if (retryCount > maxRetries) {
            throw new Error('AI processing failed after multiple attempts. This may be due to temporary server issues. Please try again in a moment, or try a different image.');
          }
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 3000 * retryCount));
        }
      }

      console.log('Extraction result:', extractResult);

      if (extractResult.status === 'success' && extractResult.output && extractResult.output.flights && extractResult.output.flights.length > 0) {
        // Validate and clean extracted data
        const validFlights = extractResult.output.flights.filter((flight) => {
          return flight.marketing_airline &&
          flight.departure_city &&
          flight.arrival_city &&
          flight.departure_date;
        });

        if (validFlights.length === 0) {
          throw new Error('No valid flight information could be extracted from the image');
        }

        // Add image URL and status to each flight, and calculate fees
        const flightsWithImage = validFlights.map((flight, index) => {
          // Only calculate fees for flights that have a total_price (usually the first flight of a journey)
          const basePrice = flight.total_price || 0;
          const serviceFeeRate = getServiceFeeRate(basePrice);
          const serviceFee = basePrice > 0 ? parseFloat((basePrice * serviceFeeRate).toFixed(2)) : 0;
          const grandTotal = basePrice > 0 ? parseFloat((basePrice + serviceFee).toFixed(2)) : 0;
          const holdFee = grandTotal > 0 ? parseFloat((grandTotal * 0.15).toFixed(2)) : 0;
          const grandTotalWithHold = grandTotal > 0 ? parseFloat((grandTotal + holdFee).toFixed(2)) : 0;
          
          return {
            ...flight,
            // Set defaults for missing fields
            flight_number: flight.flight_number || 'unknown',
            passenger_name: flight.passenger_name || 'unknown',
            seat_class: flight.seat_class || 'economy',
            total_price: basePrice, // This is now the base price without fee
            service_fee: serviceFee,
            grand_total: grandTotal,
            hold_fee: holdFee,
            grand_total_with_hold: grandTotalWithHold,
            confirmation_code: flight.confirmation_code || 'unknown',
            departure_time: flight.departure_time || '',
            arrival_time: flight.arrival_time || '',
            arrival_date: flight.arrival_date || flight.departure_date,
            connection_duration: flight.connection_duration || '', // Default for new field
            itinerary_image_url: file_url,
            // Set trip status and primary flag only for the first flight
            trip_status: index === 0 ? 'pending' : '',
            is_primary_flight: index === 0
          };
        });

        setExtractedFlight(flightsWithImage);
        setCurrentStep('review');
      } else {
        console.error('Extraction failed or returned no flights:', extractResult);
        throw new Error('Could not extract flight data from the image. Please ensure the image contains clear flight information and try again.');
      }
    } catch (err) {
      console.error('Error processing file:', err);
      
      // Provide more helpful error messages based on the error type
      let errorMessage = err.message;
      
      if (err.message.includes('database timed out') || err.message.includes('DatabaseTimeout') || err.message.includes('status code 544')) {
        errorMessage = 'The system is experiencing temporary connectivity issues. Please wait a moment and try again.';
      } else if (err.message.includes('status code 500')) {
        errorMessage = 'The server is temporarily unavailable. Please try again in a few minutes.';
      } else if (err.message.includes('extract flight data')) { // Specific error for AI extraction failure
        errorMessage = err.message;
      } else if (!err.message.includes('multiple attempts')) { // Catch all for other errors, but not if it's already a retry error
        errorMessage = `Extraction error: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveAndContinue = async () => {
    setIsProcessing(true);
    try {
      // Save all flights and collect their IDs
      const savedFlightIds = [];
      for (const flight of extractedFlight) {
        // Map extracted flight data to database booking structure
        const bookingData = {
          offer_id: `extracted_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          booking_reference: flight.confirmation_code || `REF${Date.now().toString().slice(-8)}`,
          status: 'pending',
          total_amount: flight.grand_total || flight.total_price || 0,
          total_currency: 'USD',
          passenger_count: 1,
          cabin_class: flight.seat_class || 'economy',
          flight_data: {
            marketing_airline: flight.marketing_airline,
            operating_airline: flight.operating_airline,
            flight_number: flight.flight_number,
            departure_city: flight.departure_city,
            arrival_city: flight.arrival_city,
            departure_date: flight.departure_date,
            departure_time: flight.departure_time,
            arrival_date: flight.arrival_date,
            arrival_time: flight.arrival_time,
            connection_duration: flight.connection_duration,
            passenger_name: flight.passenger_name,
            seat_class: flight.seat_class,
            total_price: flight.total_price,
            service_fee: flight.service_fee,
            grand_total: flight.grand_total,
            hold_fee: flight.hold_fee,
            grand_total_with_hold: flight.grand_total_with_hold,
            confirmation_code: flight.confirmation_code,
            itinerary_image_url: flight.itinerary_image_url,
            trip_status: flight.trip_status,
            is_primary_flight: flight.is_primary_flight
          }
        };
        
        const savedFlight = await FlightBooking.create(bookingData);
        savedFlightIds.push(savedFlight.id);
      }
      setSavedFlightId(savedFlightIds);
      setCurrentStep('passengers');
    } catch (err) {
      console.error('Error saving flight booking:', err);
      console.error('Error details:', err.message, err.stack);
      setError(`Failed to save flight booking: ${err.message || 'Unknown error'}. Please try again.`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePassengerSubmit = async (passengers) => {
    setIsProcessing(true);
    setError(null);

    try {
      // Save passengers for each flight
      for (const flightId of savedFlightId) {
        for (const passenger of passengers) {
          await Passenger.create({
            ...passenger,
            flight_booking_id: flightId
          });
        }
      }

      // Calculate the total price from extracted flights
      const totalPrice = extractedFlight.reduce((sum, flight) => sum + (flight.total_price || 0), 0);
      
      // Normalize dates - if dates are in the past, shift them to the future (next year, same month/day)
      const normalizeFutureDate = (dateString) => {
        const date = new Date(dateString);
        const today = new Date();
        
        if (date < today) {
          // Shift to next year
          const nextYear = today.getFullYear() + 1;
          date.setFullYear(nextYear);
        }
        
        return date.toISOString().split('T')[0];
      };
      
      // Helper to convert city name to approximate IATA code or use city name
      const getCityIataCode = (cityName) => {
        if (!cityName) return 'XXX';
        const cityMap = {
          'new york': 'JFK',
          'paris': 'CDG',
          'london': 'LHR',
          'los angeles': 'LAX',
          'tokyo': 'NRT',
          'dubai': 'DXB',
          'singapore': 'SIN',
          'hong kong': 'HKG',
          'san francisco': 'SFO',
          'chicago': 'ORD',
          'miami': 'MIA',
          'boston': 'BOS',
          'seattle': 'SEA',
          'atlanta': 'ATL',
          'washington': 'IAD',
          'amsterdam': 'AMS',
          'frankfurt': 'FRA',
          'rome': 'FCO',
          'madrid': 'MAD',
          'barcelona': 'BCN',
          'sydney': 'SYD',
          'melbourne': 'MEL',
          'toronto': 'YYZ',
          'vancouver': 'YVR',
          'mumbai': 'BOM',
          'delhi': 'DEL'
        };
        return cityMap[cityName.toLowerCase()] || cityName.substring(0, 3).toUpperCase();
      };

      // Calculate duration between two times
      const calculateDuration = (departTime, arriveTime) => {
        try {
          const dept = new Date(departTime);
          const arrv = new Date(arriveTime);
          const diffMs = arrv - dept;
          const hours = Math.floor(diffMs / (1000 * 60 * 60));
          const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
          return `PT${hours}H${minutes}M`;
        } catch {
          return 'PT8H30M';
        }
      };
      
      // Format the flight data for Review page to match Duffel structure
      const segments = extractedFlight.map(flight => {
        const normalizedDepartureDate = normalizeFutureDate(flight.departure_date);
        const normalizedArrivalDate = normalizeFutureDate(flight.arrival_date || flight.departure_date);
        const departingAt = `${normalizedDepartureDate}T${flight.departure_time || '00:00'}:00`;
        const arrivingAt = `${normalizedArrivalDate}T${flight.arrival_time || '00:00'}:00`;
        
        return {
          id: `seg_${Math.random().toString(36).substr(2, 9)}`,
          origin: {
            iata_code: getCityIataCode(flight.departure_city),
            city_name: flight.departure_city || 'Unknown',
            name: `${flight.departure_city} Airport`
          },
          destination: {
            iata_code: getCityIataCode(flight.arrival_city),
            city_name: flight.arrival_city || 'Unknown',
            name: `${flight.arrival_city} Airport`
          },
          departing_at: departingAt,
          arriving_at: arrivingAt,
          marketing_carrier: {
            name: flight.marketing_airline || 'Unknown Airline',
            iata_code: flight.marketing_airline?.substring(0, 2).toUpperCase() || 'XX',
            logo_symbol_url: null
          },
          operating_carrier: {
            name: flight.operating_airline || flight.marketing_airline || 'Unknown Airline',
            iata_code: flight.operating_airline?.substring(0, 2).toUpperCase() || 'XX'
          },
          marketing_carrier_flight_number: flight.flight_number || 'XXX',
          cabin_class: flight.seat_class || 'economy',
          duration: calculateDuration(departingAt, arrivingAt)
        };
      });

      const firstSegment = segments[0];
      const lastSegment = segments[segments.length - 1];

      const formattedFlightData = {
        id: savedFlightId[0],
        total_amount: totalPrice.toString(),
        total_currency: 'USD',
        owner: {
          name: firstSegment.marketing_carrier.name,
          iata_code: firstSegment.marketing_carrier.iata_code,
          logo_symbol_url: null
        },
        slices: [{
          id: `slice_${Math.random().toString(36).substr(2, 9)}`,
          origin: firstSegment.origin,
          destination: lastSegment.destination,
          duration: calculateDuration(firstSegment.departing_at, lastSegment.arriving_at),
          segments: segments
        }],
        // Store original extracted flight data
        extractedFlightData: extractedFlight
      };

      // Navigate to Review page with flight and passenger data
      navigate(createPageUrl('Review'), {
        state: {
          selectedFlight: formattedFlightData,
          passengerDetails: passengers,
          seatSelections: [],
          fromUpload: true,
          savedFlightId: savedFlightId,
          uploadedImageUrl: uploadedImage // Pass the uploaded image
        }
      });

    } catch (err) {
      console.error('Error processing passengers:', err);
      setError(err.message || "An unexpected error occurred. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRetry = () => {
    setExtractedFlight(null);
    setUploadedImage(null);
    setError(null);
    setCurrentStep('upload');
    setSavedFlightId(null);
    setShowEditModal(false);
  };

  const handleSaveChanges = (updatedFlights) => {
    setExtractedFlight(updatedFlights);
    setShowEditModal(false);
  };

  return (
    <div className=""> {/* Removed min-h-[70vh] */}
      {/* Full-screen Processing Overlay */}
      {isProcessing &&
      <motion.div
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}>

          {/* Full-screen Tunnel Animation */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="tunnel-container">
              <div className="tunnel-ring tunnel-ring-1"></div>
              <div className="tunnel-ring tunnel-ring-2"></div>
              <div className="tunnel-ring tunnel-ring-3"></div>
              <div className="tunnel-ring tunnel-ring-4"></div>
              <div className="tunnel-ring tunnel-ring-5"></div>
              <div className="tunnel-ring tunnel-ring-6"></div>
              <div className="tunnel-ring tunnel-ring-7"></div>
              <div className="tunnel-ring tunnel-ring-8"></div>
            </div>
            <div className="tunnel-particles">
              {[...Array(12)].map((_, i) => <div key={i} className={`particle particle-${i + 1}`} style={{ left: `${Math.random() * 100}%`, animationDelay: `-${Math.random() * 4}s` }}></div>)}
            </div>
          </div>

          {/* Centered Content */}
          <motion.div
            className="relative z-10 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}>

              <div className="w-20 h-20 mx-auto mb-6 glass rounded-2xl flex items-center justify-center relative overflow-hidden">
                <div className="processing-glow absolute inset-0"></div>
                <Loader2 className="w-10 h-10 text-white animate-spin relative z-10" />
              </div>
              <h3 className="text-xl font-light text-white mb-3">
                {currentStep === 'upload' && 'Transcending Your Journey'}
                {currentStep === 'review' && 'Orchestrating Your Experience'}
                {currentStep === 'passengers' && 'Materializing Your Vision'}
              </h3>
              <p className="text-gray-300 max-w-sm mx-auto mb-6 text-xs leading-relaxed">
                {currentStep === 'upload' && 'Neural networks are decoding your travel blueprint with precision...'}
                {currentStep === 'review' && 'Architecting your personalized travel framework...'}
                {currentStep === 'passengers' && 'Engineering your seamless payment experience...'}
              </p>
              <div className="flex items-center justify-center gap-2 pb-2">
                <Sparkles className="w-3 h-3 text-brand-400 animate-pulse" />
                <span className="text-gray-300 text-xs font-normal">Initializing...</span>
              </div>
            </motion.div>

          <style>{`
            .tunnel-container {
              position: absolute;
              inset: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              perspective: 1200px;
            }
            .tunnel-ring {
              position: absolute;
              border: 3px solid transparent;
              border-radius: 50%;
              background: conic-gradient(from 0deg, #7A8ABF, #4E6199, #22d3ee, #7A8ABF);
              animation: tunnel-move 3s linear infinite;
              background-clip: padding-box; /* Crucial for gradient border effect */
            }
            .tunnel-ring::before {
              content: '';
              position: absolute;
              inset: -3px;
              border-radius: 50%;
              background: linear-gradient(45deg, #7A8ABF, #4E6199, #22d3ee, #7A8ABF);
              z-index: -1;
              filter: blur(8px);
              opacity: 0.8;
            }
            .tunnel-ring-1 { width: 10vw; height: 10vw; animation-delay: 0s; }
            .tunnel-ring-2 { width: 20vw; height: 20vw; animation-delay: -0.375s; }
            .tunnel-ring-3 { width: 30vw; height: 30vw; animation-delay: -0.75s; }
            .tunnel-ring-4 { width: 40vw; height: 40vw; animation-delay: -1.125s; }
            .tunnel-ring-5 { width: 50vw; height: 50vw; animation-delay: -1.5s; }
            .tunnel-ring-6 { width: 60vw; height: 60vw; animation-delay: -1.875s; }
            .tunnel-ring-7 { width: 70vw; height: 70vw; animation-delay: -2.25s; }
            .tunnel-ring-8 { width: 80vw; height: 80vw; animation-delay: -2.625s; }
            @keyframes tunnel-move {
              0% { transform: translateZ(-800px) scale(0.1); opacity: 0; }
              50% { opacity: 1; }
              100% { transform: translateZ(800px) scale(2.5); opacity: 0; }
            }
            .tunnel-particles { position: absolute; inset: 0; pointer-events: none; }
            .particle {
              position: absolute;
              width: 5px;
              height: 5px;
              background: radial-gradient(circle, #7A8ABF, transparent);
              border-radius: 50%;
              animation: particle-float 4s linear infinite;
            }
            @keyframes particle-float {
              0% { transform: translateY(100vh) translateX(-50px) scale(0.5); opacity: 0; }
              10% { opacity: 1; }
              90% { opacity: 1; }
              100% { transform: translateY(-100px) translateX(50px) scale(1.5); opacity: 0; }
            }
            .processing-glow {
              background: radial-gradient(circle, rgba(128, 164, 250, 0.4), rgba(71, 117, 245, 0.3), transparent);
              animation: processing-pulse 2s ease-in-out infinite alternate;
            }
            @keyframes processing-pulse {
              0% { transform: scale(0.8); opacity: 0.6; }
              100% { transform: scale(1.3); opacity: 1; }
            }
          `}</style>
      </motion.div>
      }

      <div className="max-w-6xl mx-auto px-6 md:px-8 py-8">
        {/* Header */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}>

          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-white/40" />
            <span className="text-white/40 text-xs font-normal tracking-widest uppercase">
              {currentStep === 'upload' && 'AI-Powered Extraction'}
              {currentStep === 'review' && 'Verification'}
              {currentStep === 'passengers' && 'Passenger Details'}
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-light text-white mb-4 tracking-tight">
            Book a flight
          </h1>
          <p className="text-white/60 text-sm md:text-base lg:text-lg font-light">
            {currentStep === 'upload' && 'Let AI extract your flight details automatically'}
            {currentStep === 'review' && 'Verify the extracted information is correct'}
            {currentStep === 'passengers' && 'Enter passenger details for payment setup'}
          </p>
        </motion.div>

        {/* Error State */}
        {error && !isProcessing &&
        <motion.div
          className="bg-white/5 backdrop-blur-sm border border-red-500/30 rounded-3xl p-8 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}>

            <h3 className="text-xl font-light text-red-400 mb-3 tracking-tight">Error</h3>
            <p className="text-white/85 mb-6 font-normal">{error}</p>
            <Button onClick={handleRetry} variant="outline" className="text-white border-white/20 hover:bg-white/10 font-normal">
              Try Again
            </Button>
          </motion.div>
        }

        {/* Upload Step */}
        {currentStep === 'upload' && !isProcessing &&
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}>

            <FileUploadZone
            onFileSelect={handleFileSelect}
            dragActive={dragActive}
            setDragActive={setDragActive} />

          </motion.div>
        }

        {/* Review Step */}
        {currentStep === 'review' && extractedFlight && !isProcessing &&
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8">

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <h3 className="text-2xl font-light text-white flex items-center gap-3 tracking-tight">
                  <Sparkles className="w-6 h-6 text-white/60" />
                  Extracted Itinerary
                </h3>
                <p className="text-white/60 font-normal">Review details before continuing</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div>
                {extractedFlight.length > 1 ?
              <RoundtripFlightCard flights={extractedFlight} /> :

              <FlightCard flight={extractedFlight[0]} />
              }
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-6">
                 <h4 className="text-white font-light mb-4 text-center text-lg tracking-tight">Uploaded Image</h4>
                 {uploadedImage ? (
                   <img 
                     src={uploadedImage} 
                     alt="Uploaded Itinerary" 
                     className="rounded-2xl w-full h-auto object-contain border border-white/10"
                     onError={(e) => {
                       console.error('Image failed to load:', uploadedImage);
                       e.target.style.display = 'none';
                       if (e.target.nextElementSibling) {
                         e.target.nextElementSibling.style.display = 'flex';
                       }
                     }}
                   />
                 ) : null}
                 <div className="hidden flex-col items-center justify-center h-48 text-white/40">
                   <ImageIcon className="w-10 h-10 mb-3" />
                   <p className="font-normal">Image preview not available</p>
                 </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-end flex-wrap">
              <Button
              onClick={handleRetry}
              className="bg-white/10 hover:bg-white/20 text-white border border-white/10 font-normal">
                Upload Different Image
              </Button>
              <Button
              onClick={() => setShowEditModal(true)}
              className="bg-white/10 hover:bg-white/20 text-white border border-white/10 font-normal">
                <Pencil className="w-4 h-4 mr-2" />
                Edit Details
              </Button>
              <Button
              onClick={handleSaveAndContinue}
              disabled={isProcessing}
              className="bg-[var(--accent-color)] hover:bg-[var(--accent-color)]/90 text-white font-normal">
                <ArrowRight className="w-4 h-4 mr-2" />
                Continue to Passenger Details
              </Button>
            </div>
          </motion.div>
        }

        {/* Passengers Step */}
        {currentStep === 'passengers' && !isProcessing &&
        <PassengerForm
          selectedFlight={extractedFlight?.[0]}
          passengers={[{
            title: '',
            first_name: '',
            middle_name: '',
            last_name: '',
            date_of_birth: '',
            gender: '',
            passport_number: '',
            passport_expiry: '',
            nationality: '',
            phone: '',
            email: '',
            frequent_flyer_number: '',
            known_traveler_number: '',
            special_requests: '',
            seat_preference: 'window'
          }]}
          onComplete={handlePassengerSubmit}
          onCancel={() => setCurrentStep('review')}
          isProcessing={isProcessing} />

        }
      </div>
      
      <EditFlightModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        flights={extractedFlight}
        onSave={handleSaveChanges}
      />
    </div>);

}
